/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
  Stack,
  InputGroup,
  Checkbox,
  Text,
  NumberInputField,
  NumberInput,
} from '@chakra-ui/core';
import { RiFilter3Line, RiCloseFill } from 'react-icons/ri';
import { MdArrowBack } from 'react-icons/md';
import {
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from '@chakra-ui/slider';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FilterParamsType, SelectedTab, useTopCandidatesContext } from './TopCandidatesContext';
import colors from '../../../styles/colors';

export enum FilterTypes {
  SALARY = 'salary',
  SALARY_RANGE = 'salaryRange',
  OTHER_PARAMS = 'otherFilterParams',
  EXPERIENCE_RANGE = 'experienceInYears',
  LAST_ACTIVE = 'lastActivityInDays',
  FLEXIBLE_TRANSPORTATION = 'flexibleTransportation',
}

interface LocationState {
  pathname: string;
  state: {
    cameFromTopCandidate: boolean;
  };
}

export const TopCandidatesSidebarFilter = ({ onApply }: { onApply: (filters: FilterParamsType) => void }): JSX.Element => {
  const { tab, setIsFilterOpened, filterParams, setFilterParams, appliedFilter, setAppliedFilter } = useTopCandidatesContext();
  const { t } = useTranslation('businessManagement');

  const location = useLocation();
  const history = useHistory();

  const fromPage = location.state?.fromPage;

  const [isDraggingExperience, setIsDraggingExperience] = useState<boolean>(false);
  const [hasExperienceInteracted, setHasExperienceInteracted] = useState<boolean>(false);
  const [isDraggingLastActive, setIsDraggingLastActive] = useState<boolean>(false);
  const [hasLastActiveInteracted, setHasLastActiveInteracted] = useState<boolean>(false);
  const [tempFilterParams, setTempFilterParams] = useState(filterParams);
  const [tempAppliedFilter, setTempAppliedFilter] = useState<string[]>(appliedFilter);

  const showExperienceValue = isDraggingExperience || hasExperienceInteracted;
  const showLastActiveValue = isDraggingLastActive || hasLastActiveInteracted;

  const otherParamsFilterLabels = [
    // { label: 'Has applied', value: 'hasApplied' },
    { label: t('topCandidate.flexTransport'), value: 'hasFlexibleTransportation' },
    // { label: 'Has answered questions', value: 'hasAnsweredQuestions' },
    // { label: 'Availability match', value: 'availabilityMatch' },
    // { label: 'Has been rated', value: 'hasBeenRated' },
  ];

  const handleRemoveFilterItem = (item: FilterTypes | string) => {
    const updatedAppliedFilter = appliedFilter.filter((i) => i !== item);
    const updatedTempAppliedFilter = tempAppliedFilter.filter((i) => i !== item);

    setAppliedFilter(updatedAppliedFilter);
    setTempAppliedFilter(updatedTempAppliedFilter);

    setTempFilterParams((prevState) => {
      const updatedParams = { ...prevState };
      delete updatedParams[item as keyof typeof prevState];
      return updatedParams;
    });

    setFilterParams((prevState) => {
      const updatedParams = { ...prevState };
      delete updatedParams[item as keyof typeof prevState];
      return updatedParams;
    });

    // Handle special cases for filters with extra states
    if (item === FilterTypes.EXPERIENCE_RANGE) {
      setHasExperienceInteracted(false);
    }
    if (item === FilterTypes.LAST_ACTIVE) {
      setHasLastActiveInteracted(false);
    }

    // If no more filters applied, trigger API
    if (tab !== SelectedTab.BEST_MATCH && updatedAppliedFilter.length === 0) {
      onApply({});
    }
  };

  const handleFilterItemChange = (
    value: boolean | string | number | (string | number)[],
    filterType: FilterTypes,
    fieldName?: string,
  ) => {
    let updatedValue: any;

    switch (filterType) {
      case FilterTypes.SALARY_RANGE:
        if (Array.isArray(value) && value.length === 2) {
          updatedValue = { from: Number(value[0]), to: Number(value[1]) };
        }
        break;

      case FilterTypes.EXPERIENCE_RANGE:
      case FilterTypes.LAST_ACTIVE:
        updatedValue = Number(value);
        break;

      case FilterTypes.FLEXIBLE_TRANSPORTATION:
        updatedValue = value;
        break;

      default:
        updatedValue = value;
    }

    const key = fieldName || filterType;

    setTempFilterParams((prevState) => ({
      ...prevState,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      [key]: updatedValue,
    }));

    // Handle applied filters update
    if (filterType === FilterTypes.FLEXIBLE_TRANSPORTATION && !value) {
      const onlyFlexibleApplied = appliedFilter.length === 1 && appliedFilter[0] === key;

      if (onlyFlexibleApplied) {
        handleRemoveFilterItem(filterType);
      } else {
        // Remove from tempAppliedFilter and appliedFilter
        setTempAppliedFilter((prev) => prev.filter((item) => item !== key));
        setAppliedFilter((prev) => prev.filter((item) => item !== key));

        // Remove from tempFilterParams entirely
        setTempFilterParams((prevState) => {
          const updatedParams = { ...prevState };
          delete updatedParams[key as keyof typeof tempFilterParams];
          return updatedParams;
        });
      }

      return;
    }

    if (!tempAppliedFilter.includes(key)) {
      setTempAppliedFilter([...tempAppliedFilter, key]);
    }
    if (!appliedFilter.includes(key)) {
      setAppliedFilter([...appliedFilter, key]);
    }
  };

  // function to change value for salary inputs
  const handleInputChange = (index: number, value: string, filterType: FilterTypes) => {
    setTempFilterParams((prevState) => {
      const prevRange = prevState.salaryRange ?? { from: 0, to: 0 };
      const updatedRange = {
        ...prevRange,
        ...(index === 0 ? { from: Number(value) } : { to: Number(value) }),
      };

      return { ...prevState, salaryRange: updatedRange };
    });
    if (!tempAppliedFilter.includes(filterType)) {
      setTempAppliedFilter([...tempAppliedFilter, filterType]);
    }
  };

  const salaryMin = tempFilterParams?.salaryRange?.from ?? 0;
  const salaryMax = tempFilterParams?.salaryRange?.to ?? 0;

  return (
    <Box w="18%" overflowY="auto">
      <Flex px="16px" py="8px" alignItems="center" justifyContent="space-between">
        <Box
          onClick={() =>
            history.push({
              pathname: fromPage,
              state: {
                cameFromTopCandidate: true,
              },
            } as LocationState)
          }
          data-testid="backBtn"
          style={{ backgroundColor: '#fff' }}
        >
          <MdArrowBack aria-label="back-arrow" size={22} />
        </Box>
        <Flex alignItems="center" justifyContent="space-between">
          {/* <RiFilter3Line fontSize="24px" />
          <Heading as="h4" fontSize="md" ml={2}>
            {t('topCandidate.filters')}
          </Heading> */}

          <Button
            leftIcon={<RiFilter3Line fontSize="24px" />}
            onClick={() => setIsFilterOpened(false)}
            size="md"
            variant="ghost"
            fontSize="16px"
          >
            {t('topCandidate.filters')}
          </Button>
        </Flex>
      </Flex>
      <Box>
        <Flex px="16px" py="8px" alignItems="center" justifyContent="space-between">
          <Heading as="h6" lineHeight={1.1} fontWeight={700} fontSize="sm">
            {t('topCandidate.appliedFilters')}
          </Heading>
          {appliedFilter.length > 0 && (
            <Button
              onClick={() => {
                setAppliedFilter([]);
                setFilterParams({});
                setHasExperienceInteracted(false);
                setHasLastActiveInteracted(false);
                setTempFilterParams({});
                setTempAppliedFilter([]);
                if (tab !== SelectedTab.BEST_MATCH) {
                  onApply({});
                }
              }}
              size="sm"
              fontWeight="normal"
              rightIcon={<RiCloseFill />}
              colorScheme="blue"
              variant="ghost"
            >
              {t('topCandidate.resetFilters')}
            </Button>
          )}
        </Flex>
        <Box px="16px" py="8px" paddingTop={0}>
          <HStack spacing={2} flexWrap="wrap">
            {appliedFilter.map((filterItem) => (
              <Button
                key={filterItem}
                size="xs"
                fontWeight="normal"
                mb="12px"
                ml={2}
                rightIcon={<RiCloseFill onClick={() => handleRemoveFilterItem(filterItem as FilterTypes)} />}
                colorScheme="blue"
                variant="solid"
              >
                {t(`topCandidate.appliedFilterLabels.${filterItem}`)}
              </Button>
            ))}
          </HStack>
        </Box>
      </Box>
      <Box>
        <Accordion pr={4} allowMultiple defaultIndex={[0, 1, 2, 3, 4]}>
          <AccordionItem borderColor={colors.neutral[200]}>
            <h2>
              <AccordionButton _focus={{ outline: 'none' }}>
                <Box as="span" flex="1" textAlign="left" fontWeight={700} fontSize="sm">
                  {t('topCandidate.salaryRange')}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Flex alignItems="center" mb={4}>
                <Box>
                  <InputGroup>
                    <NumberInput
                      value={tempFilterParams?.salaryRange ? tempFilterParams?.salaryRange?.from : ''}
                      onChange={(e) => handleInputChange(0, e, FilterTypes.SALARY_RANGE)}
                      min={0}
                      max={tempFilterParams?.salaryRange ? tempFilterParams.salaryRange?.to : 0}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </InputGroup>
                </Box>
                <Box px={4}>-</Box>
                <Box>
                  <InputGroup>
                    <NumberInput
                      value={tempFilterParams?.salaryRange ? tempFilterParams.salaryRange?.to : ''}
                      onChange={(e) => handleInputChange(1, e, FilterTypes.SALARY_RANGE)}
                      min={tempFilterParams?.salaryRange ? tempFilterParams.salaryRange?.from : 0}
                      max={100}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </InputGroup>
                </Box>
              </Flex>
              <RangeSlider
                onChange={([from, to]) => handleFilterItemChange([from, to], FilterTypes.SALARY_RANGE)}
                ariaLabel={['min', 'max']}
                value={salaryMin < salaryMax ? [salaryMin, salaryMax] : [0, 0]}
              >
                <RangeSliderTrack bg={colors.lavenderMist}>
                  <RangeSliderFilledTrack height="100%" backgroundColor="blue.500" />
                </RangeSliderTrack>
                <RangeSliderThumb index={0} borderColor="blue.500" />
                <RangeSliderThumb index={1} borderColor="blue.500" />
              </RangeSlider>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem borderColor={colors.neutral[200]}>
            <h2>
              <AccordionButton _focus={{ outline: 'none' }}>
                <Box as="span" flex="1" textAlign="left" fontWeight={700} fontSize="sm">
                  {t('topCandidate.filters')}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            {/* <AccordionPanel pb={4}>
              <CheckboxGroup
                colorScheme="blue"
                onChange={(data) => handleFilterItemChange(data, FilterTypes.OTHER_PARAMS)}
                value={tempFilterParams.otherFilterParams || []}
              >
                <Stack spacing={3} direction="column">
                  {otherParamsFilterLabels.map((param) => (
                    <Checkbox key={param.value} value={param.value}>
                      <Text fontSize={{ base: '11px', md: '12px', lg: '14px' }}>{param.label}</Text>
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </AccordionPanel> */}

            <AccordionPanel pb={4}>
              <Stack spacing={3} direction="column">
                {otherParamsFilterLabels.map((param) => (
                  <Checkbox
                    key={param.value}
                    isChecked={tempFilterParams.flexibleTransportation || false}
                    onChange={(e) =>
                      handleFilterItemChange(e.target.checked, FilterTypes.FLEXIBLE_TRANSPORTATION, 'flexibleTransportation')
                    }
                  >
                    <Text fontSize={{ base: '11px', md: '12px', lg: '14px' }}>{param.label}</Text>
                  </Checkbox>
                ))}
              </Stack>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem borderColor={colors.neutral[200]}>
            <h2>
              <AccordionButton _focus={{ outline: 'none' }}>
                <Box as="span" flex="1" textAlign="left" fontWeight={700} fontSize="sm">
                  {t('topCandidate.minExperience')}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <Box>
                <Flex align="flex-end" minH="40px" pt={5}>
                  <Text fontSize="sm" mr={2} minW="20px">
                    0
                  </Text>

                  {/* Slider */}
                  <Box flex="1" position="relative">
                    <Slider
                      min={0}
                      max={10}
                      value={tempFilterParams.experienceInYears ? tempFilterParams.experienceInYears : 0}
                      onChange={(value) => handleFilterItemChange([value], FilterTypes.EXPERIENCE_RANGE)}
                      onChangeStart={() => {
                        setIsDraggingExperience(true);
                        setHasExperienceInteracted(true);
                      }}
                      onChangeEnd={() => setIsDraggingExperience(false)}
                    >
                      <SliderTrack bg={colors.lavenderMist}>
                        <SliderFilledTrack height="100%" backgroundColor="blue.500" />
                      </SliderTrack>
                      <SliderThumb borderColor="blue.500">
                        {showExperienceValue && (
                          <Box
                            position="absolute"
                            top="-30px"
                            transform="translateX(-50%)"
                            bg="blue.500"
                            color="white"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                            fontSize="xs"
                            fontWeight={700}
                            whiteSpace="nowrap"
                          >
                            {tempFilterParams.experienceInYears ? tempFilterParams.experienceInYears : 0}
                          </Box>
                        )}
                      </SliderThumb>
                    </Slider>
                  </Box>

                  <Text fontSize="sm" ml={2} minW="20px" textAlign="right">
                    10
                  </Text>
                </Flex>
              </Box>
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem borderColor={colors.neutral[200]}>
            <h2>
              <AccordionButton _focus={{ outline: 'none' }}>
                <Box as="span" flex="1" textAlign="left" fontWeight={700} fontSize="sm">
                  {t('topCandidate.lastActiveDays')}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>

            <AccordionPanel pb={4}>
              <Box>
                <Flex align="flex-end" minH="40px" pt={5}>
                  <Text fontSize="sm" mr={2} minW="20px">
                    0
                  </Text>

                  {/* Slider */}
                  <Box flex="1" position="relative">
                    <Slider
                      min={0}
                      max={30}
                      value={tempFilterParams.lastActivityInDays ? tempFilterParams.lastActivityInDays : 0}
                      onChange={(value) => handleFilterItemChange([value], FilterTypes.LAST_ACTIVE)}
                      onChangeStart={() => {
                        setIsDraggingLastActive(true);
                        setHasLastActiveInteracted(true);
                      }}
                      onChangeEnd={() => setIsDraggingLastActive(false)}
                    >
                      <SliderTrack bg={colors.lavenderMist}>
                        <SliderFilledTrack height="100%" backgroundColor="blue.500" />
                      </SliderTrack>
                      <SliderThumb borderColor="blue.500">
                        {showLastActiveValue && (
                          <Box
                            position="absolute"
                            top="-30px"
                            transform="translateX(-50%)"
                            bg="blue.500"
                            color="white"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                            fontSize="xs"
                            fontWeight={700}
                            whiteSpace="nowrap"
                          >
                            {tempFilterParams.lastActivityInDays ? tempFilterParams.lastActivityInDays : 0}
                          </Box>
                        )}
                      </SliderThumb>
                    </Slider>
                  </Box>

                  <Text fontSize="sm" ml={2} minW="20px" textAlign="right">
                    30
                  </Text>
                </Flex>
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
        <Box>
          <Flex alignItems="center" justifyContent="space-between" px="16px" py="10px">
            {tempAppliedFilter.length > 0 && (
              <>
                <Button
                  onClick={() => {
                    setAppliedFilter([]);
                    setFilterParams({});
                    setTempFilterParams({});
                    setTempAppliedFilter([]);
                    setHasExperienceInteracted(false);
                    setHasLastActiveInteracted(false);
                    if (tab !== SelectedTab.BEST_MATCH) {
                      onApply({});
                    }
                  }}
                  colorScheme="blue"
                  variant="ghost"
                >
                  {t('topCandidate.reset')}
                </Button>

                <Button
                  colorScheme="blue"
                  onClick={() => {
                    setAppliedFilter(tempAppliedFilter);
                    setFilterParams(tempFilterParams);
                    if (tab !== SelectedTab.BEST_MATCH) {
                      onApply(tempFilterParams);
                    }
                  }}
                >
                  {t('topCandidate.applyBtn')}
                </Button>
              </>
            )}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};
